import { Component, HostListener, Input } from '@angular/core';
import { AssetDto } from '@picflow/types';
import { Rect } from 'src/app/classes/rect.class';
import { ScreenSizes } from 'src/app/classes/screen-sizes.class';
import { devicePixelRatio } from 'src/app/lib/device-pixel-ratio';
import { GetBounds } from 'src/app/lib/get-bounds';
import { isMetaKey } from 'src/app/lib/is-meta-key';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { AnnotationColor } from '../../types/annotation-color';
import { AnnotationTool } from '../../types/annotation-tool';
import { OtherShape, PolygonShape, Shape } from '../../types/shapes';
import { ViewAnnotationComponent } from '../view-annotation/view-annotation.component';

@Component({
  selector: 'app-create-annotation',
  templateUrl: './create-annotation.component.html',
  styleUrls: ['./create-annotation.component.scss'],
  standalone: true,
  imports: [ViewAnnotationComponent],
})
export class CreateAnnotationComponent {
  @Input() offsetX: number;
  @Input() offsetY: number;
  @Input() scale: number;
  @Input() offsetScale: number;
  @Input() sideMenuOpen: boolean;
  @Input() asset: AssetDto;
  @Input() currentTool: AnnotationTool;
  @Input() currentColor: AnnotationColor;
  @Input() noAnimate: boolean;
  currentShape: OtherShape | PolygonShape;
  shapes: Shape[] = [];
  deletedShapes: Shape[] = [];
  blockMode: boolean;
  public drawing = false;

  @HostListener('window:keydown', ['$event']) keyDownEvent(event: KeyboardEvent) {
    const metaKey = isMetaKey(event);
    if (['z', 'y'].includes(event.key) && metaKey) {
      event.preventDefault();
      this.analytics.reportKeyboardShortcut(!event.shiftKey ? 'undo' : 'redo', 'create-annotation');

      this.changeHistory(event.key === 'z' && !event.shiftKey ? 'undo' : 'redo');
    }
    if (event.code === 'Space') this.blockMode = false;
  }
  @HostListener('window:keyup', ['$event']) keyUpEvent(event: KeyboardEvent) {
    const metaKey = isMetaKey(event);
    if (['z', 'y'].includes(event.key) && metaKey) {
      event.preventDefault();
      this.analytics.reportKeyboardShortcut(!event.shiftKey ? 'undo' : 'redo', 'create-annotation');

      this.changeHistory(event.key === 'z' && !event.shiftKey ? 'undo' : 'redo');
    }
    if (event.code === 'Space') this.blockMode = true;
  }
  @HostListener('window:mousedown', ['$event']) mouseDownEvent(event: MouseEvent) {
    if (event.buttons === 1 || event.buttons === 4) this.blockMode = false;
  }
  @HostListener('window:mouseup', ['$event']) mouseUpEvent() {
    this.blockMode = true;
  }

  constructor(private analytics: AnalyticsService) {
    this.blockMode = ScreenSizes.screenSize() === 'desktop';
  }

  onMouseDown(event: MouseEvent) {
    if (!this.blockMode) return;
    if (event.buttons !== 1) return;
    event.preventDefault();
    event.stopPropagation();
    this.onMove(Rect.fromMouseEvent(event));
    this.drawing = true;
  }

  onMouseMove(event: MouseEvent) {
    if (this.drawing) this.drawing = false;
    if (!this.blockMode) return;
    if (event.buttons !== 1) return;
    event.preventDefault();
    event.stopPropagation();
    this.onMove(Rect.fromMouseEvent(event));
    this.drawing = true;
  }

  onMouseUp(event: MouseEvent) {
    if (!this.blockMode) return;
    event.preventDefault();
    event.stopPropagation();
    this.onEnd();
    if (this.drawing) this.drawing = false;
  }

  onMove(movedTo: Rect) {
    const bounds = GetBounds(this.offsetScale, this.scale, this.sideMenuOpen, this.asset, this.offsetX, this.offsetY);
    const x = (1 / bounds.width) * Math.round(movedTo.x - bounds.x);
    const y = (1 / bounds.height) * Math.round(movedTo.y - bounds.y);

    if (this.currentShape) {
      if (this.currentShape.tool === 'polygon') {
        this.currentShape.points.push(x);
        this.currentShape.points.push(y);
      } else {
        this.currentShape.x2 = x;
        this.currentShape.y2 = y;
      }
    } else {
      if (this.currentTool === 'polygon') {
        this.currentShape = {
          tool: 'polygon',
          color: this.currentColor,
          points: [x, y],
        };
        this.shapes.push(this.currentShape);
      } else {
        this.currentShape = {
          tool: this.currentTool,
          color: this.currentColor,
          x1: x,
          y1: y,
          x2: x,
          y2: y,
        };
        this.shapes.push(this.currentShape);
      }
    }
  }

  onEnd() {
    if (!this.currentShape) return;
    this.deletedShapes = [];
    const shape = this.currentShape;
    if (shape.tool === 'polygon') {
      const accuracy = 1000000;
      let rects: Rect[] = [];
      for (let i = 0; i < shape.points.length; i += 2) {
        const rect = new Rect(
          Math.round(shape.points[i] * accuracy) / accuracy,
          Math.round(shape.points[i + 1] * accuracy) / accuracy
        );
        rects.push(rect);
      }

      const newRects: Rect[] = [];

      rects.forEach((rect, i) => {
        const previous = newRects[newRects.length - 1];
        const next = rects[i + 1];
        if (!previous || !next) return newRects.push(rect);

        const data = this.asset.version_data.data || this.getDataBackup();

        const pos = new Rect(rect.x * data.width, rect.y * data.height);
        const previousPos = new Rect(previous.x * data.width, previous.y * data.height);
        const nextPos = new Rect(next.x * data.width, next.y * data.height);

        const diffWithPrevious = pos.subtract(previousPos);
        const distFromPrevious = diffWithPrevious.magnitude();

        if (distFromPrevious < 4) return;

        const diffToNext = nextPos.subtract(pos);
        const backAngle = diffWithPrevious.toAngle();
        const forwardAngle = diffToNext.toAngle();

        const percent = 1 - rect.subtract(previous).magnitude();
        if (Math.abs(backAngle - forwardAngle) < percent * 2) return;
        newRects.push(rect);
      });

      shape.points = [];
      newRects.forEach(rect => {
        shape.points.push(rect.x);
        shape.points.push(rect.y);
      });
      this.currentShape = undefined;
    } else {
      const diff = new Rect(shape.x2, shape.y2).subtract(new Rect(shape.x1, shape.y1));
      if (diff.magnitude() === 0) this.shapes.pop();
      this.currentShape = undefined;
    }
  }

  getDataBackup() {
    return { width: 250 * devicePixelRatio(), height: 250 * devicePixelRatio() };
  }

  public changeHistory(dir: 'undo' | 'redo') {
    if (dir === 'undo' && this.shapes.length) this.deletedShapes.push(this.shapes.pop());
    if (dir === 'redo' && this.deletedShapes.length) this.shapes.push(this.deletedShapes.pop());
  }
}
